import { AppwriteException, Query } from 'appwrite';
import { gunzipSync } from 'fflate';

import { appwriteConfig } from '@/config/appwrite';
import { databases, downloadFileBytes, storage } from '@/lib/appwrite';
import { sessionSummaryFromRow, sessionTimestamp, type SessionRow, type SessionSummary } from './model';

/**
 * Port of `session_repository.dart`.
 *
 * The query is scoped to the caller's own `user_id`; Appwrite's row-level
 * permissions enforce the same boundary server-side, so a mismatch simply
 * returns nothing rather than another user's data.
 */

/** Result of a list query, including how many rows had to be skipped. */
export interface SessionSummariesPage {
  readonly sessions: readonly SessionSummary[];
  /** Number of rows that were unreadable and left out of `sessions`. */
  readonly skipped: number;
}

/** Appwrite batch size, matching the Flutter repository. */
const PAGE_LIMIT = 100;

/** Loads all sessions belonging to `userId`, most recent first. */
export async function listSessions(userId: string): Promise<SessionSummariesPage> {
  const all: SessionSummary[] = [];
  let skipped = 0;
  let offset = 0;

  try {
    for (;;) {
      const result = await databases.listDocuments({
        databaseId: appwriteConfig.databaseId,
        collectionId: appwriteConfig.sessionSummariesCollectionId,
        queries: [
          Query.equal('user_id', userId),
          Query.orderDesc('$createdAt'),
          Query.limit(PAGE_LIMIT),
          Query.offset(offset),
        ],
      });

      for (const document of result.documents) {
        const session = sessionSummaryFromRow(document as unknown as SessionRow);
        if (session === null) {
          skipped += 1;
          continue;
        }
        all.push(session);
      }

      if (result.documents.length < PAGE_LIMIT) break;
      offset += PAGE_LIMIT;
    }
  } catch (error) {
    // If some sessions are already in hand, return them rather than failing
    // the whole screen; only a failure on the first batch propagates.
    if (all.length === 0) throw error;
    console.warn('Error fetching subsequent session batches:', error);
  }

  // Newest first, with undated rows sinking to the bottom rather than to the
  // top — `orderDesc('$createdAt')` alone cannot express that.
  all.sort((a, b) => {
    const x = sessionTimestamp(a);
    const y = sessionTimestamp(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return y.getTime() - x.getTime();
  });

  return { sessions: all, skipped };
}

/** Raised when a session has no raw file, or the file is gone. */
export class EegRawDataUnavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EegRawDataUnavailable';
  }
}

export interface StoredFile {
  readonly id: string;
  readonly name: string;
  readonly sizeOriginal: number;
  readonly mimeType: string;
  readonly createdAt: string;
}

/** Metadata for a file in the EEG bucket. */
export async function getEegFileMeta(fileId: string): Promise<StoredFile> {
  try {
    const meta = await storage.getFile({ bucketId: appwriteConfig.eegBucketId, fileId });
    return {
      id: meta.$id,
      name: meta.name !== '' ? meta.name : fileId,
      sizeOriginal: meta.sizeOriginal,
      mimeType: meta.mimeType,
      createdAt: meta.$createdAt,
    };
  } catch (error) {
    throw translateStorageError(error);
  }
}

export interface EegRawDataFile {
  readonly name: string;
  readonly bytes: ArrayBuffer;
}

/**
 * Downloads the file with `fileId` from the EEG storage bucket.
 *
 * Throws `EegRawDataUnavailable` when the file no longer exists or is not
 * readable by this user — the same contract as the Flutter repository.
 */
export async function fetchEegRawData(fileId: string): Promise<EegRawDataFile> {
  const meta = await getEegFileMeta(fileId);

  let bytes: ArrayBuffer;
  try {
    bytes = await downloadFileBytes(appwriteConfig.eegBucketId, fileId);
  } catch (error) {
    throw translateStorageError(error);
  }

  if (bytes.byteLength === 0) {
    throw new EegRawDataUnavailable('The raw data file is empty.');
  }

  return { name: meta.name, bytes };
}

function translateStorageError(error: unknown, noun = 'raw data file'): unknown {
  if (error instanceof AppwriteException) {
    if (error.code === 404) {
      return new EegRawDataUnavailable(
        `The ${noun} for this session is no longer available.`,
      );
    }
    if (error.code === 401 || error.code === 403) {
      return new EegRawDataUnavailable(`You do not have permission to open this ${noun}.`);
    }
  }
  return error;
}

// ---------------------------------------------------------------------------
// Timeline file (`timeline_file_id` → `<session>.timeline.jsonl.gz`)

export interface EegTimelineFile {
  /** Stored name, e.g. `abc.timeline.jsonl.gz`. */
  readonly storedName: string;
  /** Name with `.gz` stripped, e.g. `abc.timeline.jsonl`. */
  readonly name: string;
  /** The decompressed JSONL text. */
  readonly text: string;
  /** Number of non-empty JSONL records. */
  readonly lineCount: number;
}

const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;

/**
 * Downloads the session's timeline file from the EEG bucket and gunzips it.
 *
 * The file is stored as `.timeline.jsonl.gz`; a file that is not actually
 * gzip-compressed (no magic bytes) is passed through as-is so an uncompressed
 * upload still exports cleanly.
 */
export async function fetchEegTimelineFile(fileId: string): Promise<EegTimelineFile> {
  let storedName: string;
  let bytes: ArrayBuffer;
  try {
    storedName = (await getEegFileMeta(fileId)).name;
    bytes = await downloadFileBytes(appwriteConfig.eegBucketId, fileId);
  } catch (error) {
    throw translateStorageError(error, 'timeline file');
  }

  if (bytes.byteLength === 0) {
    throw new EegRawDataUnavailable('The timeline file is empty.');
  }

  const raw = new Uint8Array(bytes);
  const isGzip = raw.length > 2 && raw[0] === GZIP_MAGIC_0 && raw[1] === GZIP_MAGIC_1;
  let payload: Uint8Array;
  try {
    payload = isGzip ? gunzipSync(raw) : raw;
  } catch (error) {
    console.warn(`GZIP decompression failed for ${storedName}:`, error);
    throw new EegRawDataUnavailable('The timeline file could not be decompressed — it may be corrupt.');
  }

  const text = new TextDecoder('utf-8').decode(payload);
  const lineCount = text.split('\n').filter((line) => line.trim() !== '').length;
  const name = storedName.toLowerCase().endsWith('.gz') ? storedName.slice(0, -3) : storedName;

  return { storedName, name, text, lineCount };
}
